// react
import styled, { css } from 'styled-components';

import { Card, Icon } from '@/components/atoms';
import { Icons } from '@/components/atoms/Icon';
import { ReferralFormType } from '@karehero/models';

interface ReferralProcessProps {
  className?: string;
  formType?: ReferralFormType;
}

const CardWithIcon = ({
  stepLabel,
  subTitle,
  icon,
  content,
}: {
  stepLabel: string;
  subTitle: string;
  icon: Icons;
  content: string;
}) => {
  return (
    <Card color='none' elevation={0} isBordered={false} isPaddingSm>
      <Wrapper>
        <IconWithLabelWrapper>
          <IconWrapper>
            <Icon icon={icon} fontSize={48} />
          </IconWrapper>
          <StepLabel>{stepLabel}</StepLabel>
        </IconWithLabelWrapper>
        <SubTitle>{subTitle}</SubTitle>
        <Content>{content}</Content>
      </Wrapper>
    </Card>
  );
};

const SjpReferralProcess = ({ className }: ReferralProcessProps) => {
  return (
    <CardContainer className={className}>
      <CardWithIcon
        icon='memo'
        stepLabel='Step 1'
        subTitle='Refer your client'
        content='Invite your client to KareHero by completing the form below'
      />
      <CardWithIcon
        icon='envelope'
        stepLabel='Step 2'
        subTitle='Client receives invite'
        content='Personalised email invite gives access to KareHero and schedules an initial consultation with the care team'
      />
      <CardWithIcon
        icon='check-circle'
        stepLabel='Step 3'
        subTitle='Track & Monitor'
        content="Track your client's registration, monitor their needs, and access resources via your Partner Portal"
      />
    </CardContainer>
  );
};

const IfaReferralProcess = ({ className }: ReferralProcessProps) => {
  return (
    <CardContainer className={className}>
      <CardWithIcon
        icon='memo'
        stepLabel='Step 1'
        subTitle='Refer your client'
        content='Invite your client to KareHero by completing the form below and agreeing to the payment terms.'
      />
      <CardWithIcon
        icon='envelope'
        stepLabel='Step 2'
        subTitle='Client receives invite'
        content='Your client receives a personalised email invite with full access to KareHero and dedicated 1:1 support.'
      />
      <CardWithIcon
        icon='check-circle'
        stepLabel='Step 3'
        subTitle='Track & Monitor'
        content="Track your client's registration and progress via your Portal homepage."
      />
    </CardContainer>
  );
};

/**
 * ReferralProcess describes the steps for a partner making a referral
 */
export const ReferralProcess = ({
  formType,
  ...props
}: ReferralProcessProps) => {
  return formType === ReferralFormType.SjpIFA ? (
    <SjpReferralProcess {...props} />
  ) : (
    <IfaReferralProcess {...props} />
  );
};

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  align-items: center;
  ${(props) =>
    props.theme.breakpoint.sm(css`
      flex-direction: row;
      align-items: flex-start;
    `)}
`;

const SubTitle = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.primary[80]};
  text-align: left;
`;

const StepLabel = styled.div`
  font: ${(props) => props.theme.typography.body.md.medium};
  text-align: left;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[8]};
  max-width: 250px;
  align-items: left;
`;

const IconWithLabelWrapper = styled.div`
  color: ${(props) => props.theme.color.cards.red.tertiary};
  display: flex;
  flex-direction: row;
  text-align: left;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const IconWrapper = styled.div`
  border-radius: 99999px;
  align-items: left;
`;

const Content = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[8]};
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: left;
  align-items: left;
`;

ReferralProcess.displayName = 'ReferralProcess';

export default ReferralProcess;
